import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, RefreshCw, ShieldCheck, X } from 'lucide-react';
import type { CaptchaAnswer, CaptchaChallenge } from '../../types';
import { queueAccessService } from '../../services/queueAccessService';
import { SliderPuzzleCaptcha } from './SliderPuzzleCaptcha';
import { ClickInOrderCaptcha } from './ClickInOrderCaptcha';
import { RotateUprightCaptcha } from './RotateUprightCaptcha';
import '../../styles/captcha.css';

interface CaptchaModalProps {
  onSolved: (passToken: string) => void;
  onCancel: () => void;
}

const serverMessage = (error: any): string | undefined => error?.response?.data?.message;

/**
 * Loads a challenge, lets the user solve it and exchanges the answer for a pass token.
 * Every attempt uses a fresh challenge (the server accepts one answer per challenge).
 */
export function CaptchaModal({ onSolved, onCancel }: CaptchaModalProps) {
  const { t } = useTranslation();
  const [challenge, setChallenge] = useState<CaptchaChallenge | null>(null);
  const [startedAt, setStartedAt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState(false);

  const loadChallenge = useCallback(async () => {
    setLoading(true);
    setChallenge(null);
    try {
      const next = await queueAccessService.getChallenge();
      setChallenge(next);
      setStartedAt(performance.now());
    } catch (err: any) {
      setBlocked(err?.response?.status === 429);
      setError(serverMessage(err) ?? t('captcha.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);

  const handleSubmit = async (answer: CaptchaAnswer) => {
    if (!challenge) return;
    setVerifying(true);
    setError(null);
    try {
      const passToken = await queueAccessService.verify(challenge.challengeId, answer);
      onSolved(passToken);
    } catch (err: any) {
      setError(serverMessage(err) ?? t('captcha.failed'));
      await loadChallenge();
    } finally {
      setVerifying(false);
    }
  };

  const viewProps = challenge && {
    // Remount per challenge so component state (drag position, taps) resets
    key: challenge.challengeId,
    challenge,
    startedAt,
    disabled: verifying,
    onSubmit: handleSubmit,
  };

  return (
    <div className="modal-overlay-modern captcha-overlay">
      <div className="modal-modern captcha-modal" role="dialog" aria-modal="true" aria-labelledby="captcha-title">
        <div className="modal-header-modern">
          <h3 className="modal-title" id="captcha-title">
            <ShieldCheck size={20} /> {t('captcha.title')}
          </h3>
          <p className="modal-subtitle">{t('captcha.subtitle')}</p>
          <button className="modal-close-btn" onClick={onCancel} aria-label={t('common.cancel')}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-content-modern captcha-body">
          {loading && (
            <div className="captcha-loading">
              <Clock className="animate-spin" size={24} />
            </div>
          )}

          {viewProps?.challenge.type === 'SliderPuzzle' && <SliderPuzzleCaptcha {...viewProps} />}
          {viewProps?.challenge.type === 'ClickInOrder' && <ClickInOrderCaptcha {...viewProps} />}
          {viewProps?.challenge.type === 'RotateUpright' && <RotateUprightCaptcha {...viewProps} />}

          {error && (
            <div className="error-alert captcha-error" role="alert">
              <X size={16} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-actions-modern">
          <button className="btn-modern btn-secondary-modern" onClick={onCancel}>
            {t('common.cancel')}
          </button>
          <button
            className="btn-modern btn-secondary-modern"
            onClick={() => {
              setError(null);
              loadChallenge();
            }}
            disabled={loading || verifying || blocked}
          >
            <RefreshCw size={16} /> {t('captcha.newPuzzle')}
          </button>
        </div>
      </div>
    </div>
  );
}
